/**
 * components/TxnBarangStokCell.js
 * Shared "Barang" + "Stok" pair for Penjualan.js/Pembelian.js — dashboard alignment fix.
 *
 * Replaces the old pair of independent elements (`TxnBarangCell.js`'s own `<td>` + each page's
 * own hand-written Stok `<td>`) with ONE `<td colSpan={2}>` containing a small nested row-per-
 * item structure — Barang's name and Stok's text for the SAME item are literal siblings inside
 * one row, one row per item. A real-Chrome investigation found the old pair could drift out of
 * visual alignment: both columns rendered independent stacks of `<div>`s, one per item, staying
 * paired ONLY by coincidence — as soon as one column's div for item i was taller than the other
 * column's div for item i (e.g. a long item name wrapping to several lines while its stock text
 * stayed one line), every item AFTER that point drifted, since nothing tied the two stacks
 * together. A row's own layout algorithm guarantees both its cells start at the same top edge
 * regardless of how tall either one's content grows — this is the browser's own row-layout
 * mechanism, not something CSS/JS has to detect or compute, and it holds unconditionally (no
 * "does this name look long enough to need pairing" check, which the fix brief explicitly
 * rejected as fragile).
 *
 * **Deliberately built with `display: table`/`table-row`/`table-cell` on plain `<div>`s, NOT a
 * literal nested `<table>`/`<tr>`/`<td>`** — an earlier version used a real nested table and was
 * caught by the existing test suite: this app's own tests (and likely other DOM traversal)
 * broadly use `"tbody tr"` to count/find a table's rows, a selector that matches ANY `<tr>`
 * descendant regardless of nesting depth — a literal inner `<table>` silently doubled every row
 * count on Penjualan/Pembelian (`container.querySelectorAll("tbody tr")` picked up the inner
 * rows too). `display: table-row`/`table-cell` on `<div>`s gives the IDENTICAL layout guarantee
 * (browsers treat CSS-table-display boxes the same as real table elements for layout purposes,
 * including `table-layout: fixed` column-width behavior) with zero real `<tr>`/`<td>` elements
 * anywhere in the subtree, so it can never collide with a `tbody tr`-style selector again.
 *
 * Renders inside `.txn-table--fixed` (Penjualan.js/Pembelian.js's own `table-layout: fixed`
 * modifier) — the outer `<colgroup>` still declares TWO separate `<col>` entries (Barang, Stok)
 * even though the body never renders a literal second `<td>` for Stok, matching `<thead>`'s two
 * `<th>`s; a `colSpan={2}` cell under `table-layout: fixed` takes the sum of its spanned `<col>`
 * widths (standard CSS2.1 behavior), so this cell's total width is deterministic.
 *
 * **`stokWidth` (required prop) is the SAME number the caller's own outer `<colgroup>` gives
 * Stok's `<col>`** — this is load-bearing, not redundant: the outer colgroup's Barang/Stok
 * widths only govern the header `<th>`s and the merged cell's own TOTAL width (the sum); they do
 * NOT automatically govern how that total splits internally between the name/stok divs below —
 * `.barangstok-inner` runs its OWN independent `table-layout: fixed` pass on whatever width it's
 * given `.barangstok-inner__stok`, using CSS-table's normal "first row's declared widths become
 * the column widths" rule. A first version of this component hardcoded that split via a SEPARATE
 * CSS class rule (`.barangstok-inner__stok { width: 130px }`) instead of taking it as a prop —
 * a real, caught regression: it silently disagreed with the outer colgroup's own Stok width
 * (110px in both callers), so the header's "Stok" label and the body's actual stock column no
 * longer lined up, and the two numbers had no way to stay in sync on a future width tweak. Both
 * `Penjualan.js`/`Pembelian.js` now define ONE local `STOK_COL_WIDTH` constant and pass it to
 * BOTH the outer `<col>` and this prop, so there is exactly one place either page ever writes
 * that number — restoring the same "one declaration, no drift risk" property the nested-`<table>`
 * design (see the header comment above) was originally chosen for.
 *
 * `renderStok(line, index)` is a render prop so this component stays type-agnostic — Penjualan's
 * TRANSFER rows need a dual-bucket `−N PCS X → +N PCS Y` two-span line; Pembelian's is always a
 * plain single `+N PCS` span. Both render inside the SAME paired `.barangstok-inner__stok` cell,
 * so the pairing guarantee holds for either shape, including a TRANSFER line wrapping under a
 * narrow Stok column.
 *
 * **Barang column width — recalibrated against real catalog data (dashboard alignment fix,
 * round 3), not the rare worst case.** The outer `<colgroup>`'s Barang `<col>` (owned by
 * `Penjualan.js`/`Pembelian.js`, not this file — `.barangstok-inner__name` itself absorbs
 * whatever's left of that declared width, per the "one flexible column" mechanism above) was
 * `230px`, originally aimed at the real catalog's rare 35-character maximum name length — a
 * real-Chrome text measurement during round 3 found this was never actually correct: a genuine
 * 35-char name needs ~254px of ink width alone (+24px of this cell's own `padding: 0 12px` =
 * ~278px), so 230px never actually fit the true max unwrapped either. Recalibrated instead
 * toward the real catalog's own p90-p95 name-length range (25-27 chars, ~1,179 unique real item
 * names, median 17/p90 25/p95 27/max 35 — see the root `CLAUDE.md`'s "row-budget slack
 * investigation" for the original catalog scan, though that scan was for a different, monospace
 * print-document font; this measurement is fresh, for THIS table's real Segoe UI 14px/400) —
 * a real 26-char name measures 186px ink width in this exact cell context (detached-probe
 * `Range.getBoundingClientRect()`, not `getComputedStyle`, not guessed), +24px padding = 210px,
 * +~5px real-rendering margin = **215px**, the new `<col>` value. A median (17-char) name's own
 * ink width (~124px) is unaffected by the column's declared width — only the leftover gap
 * before Stok shrinks, by exactly the 15px the column itself shrunk (82px gap at the old 230px
 * down to 67px at 215px, real-measured). The accepted tradeoff, unchanged from round 1's own
 * framing: a rare name beyond the low-to-mid 20s now wraps onto a second line slightly more
 * often than before — never clips, matching every other column's own wrap-not-clip guarantee
 * in this table.
 */
import React from "react";

export default function TxnBarangStokCell({ lines = [], byCode, renderStok, stokWidth }) {
  return (
    <td colSpan={2} className="barangstok-cell">
      <div className="barangstok-inner">
        {lines.map((l, i) => (
          <div key={i} className="barangstok-inner__row">
            <div className="barangstok-inner__name">
              {l.name || byCode.get(l.internalCode)?.name || l.internalCode}
            </div>
            <div className="barangstok-inner__stok" style={{ width: stokWidth }}>{renderStok(l, i)}</div>
          </div>
        ))}
      </div>
    </td>
  );
}
